import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import SEOService from '../../services/SEOService';

const SEOHead = ({ 
  customMeta = {}, 
  boatId = null, 
  postSlug = null,
  pageType = null 
}) => {
  const [seoData, setSeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchSEO = async () => {
      try {
        setLoading(true);
        let metaData;
        
        // Si se proporciona metadata personalizada, usarla
        if (Object.keys(customMeta).length > 0) {
          metaData = customMeta;
        }
        // Si se especifica un tipo de página
        else if (pageType) {
          metaData = await SEOService.getStaticPageSEO(pageType);
        }
        // Si se proporciona boatId
        else if (boatId) {
          metaData = await SEOService.getBoatSEO(boatId);
        }
        // Si se proporciona postSlug
        else if (postSlug) {
          metaData = await SEOService.getBlogPostSEO(postSlug);
        }
        // Obtener SEO basado en la ruta actual
        else {
          const params = { boatId, postSlug };
          metaData = await SEOService.getSEOForPath(location.pathname, params);
        }
        setSeoData(metaData);
      } catch (error) {
        console.error('Error fetching SEO data:', error);
        // Usar metadata por defecto en caso de error
        const defaultMeta = {
          title: 'Mr. Sea Miami - Boat Rentals',
          description: 'Premium boat rentals in Miami. Experience the best of Miami waters with our luxury boat fleet.',
          og_title: 'Mr. Sea Miami - Boat Rentals',
          og_description: 'Premium boat rentals in Miami. Experience the best of Miami waters with our luxury boat fleet.',
          og_image: '',
          og_type: 'website',
          twitter_card: 'summary_large_image',
          twitter_title: 'Mr. Sea Miami - Boat Rentals',
          twitter_description: 'Premium boat rentals in Miami. Experience the best of Miami waters with our luxury boat fleet.',
          twitter_image: '',
          canonical_url: window.location.href,
          og_url: window.location.href,
        };

        setSeoData(defaultMeta);
      } finally {
        setLoading(false);
      }
    };

    fetchSEO();
  }, []); // Solo ejecutar una vez cuando el componente se monta

  // No renderizar nada mientras carga
  if (loading || !seoData) {
    return null;
  }

  return (
    <Helmet>
      {/* Meta tags básicos */}
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />
      <meta name="keywords" content="Mr. Sea Miami, boat rental, Miami, yacht rental, luxury boats" />
      <meta name="author" content="Mr. Sea Miami" />
      <meta name="robots" content="index, follow" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={seoData.canonical_url || window.location.href} />
      
      {/* Open Graph tags */}
      <meta property="og:title" content={seoData.og_title || seoData.title} />
      <meta property="og:description" content={seoData.og_description || seoData.description} />
      <meta property="og:image" content={seoData.og_image} />
      <meta property="og:url" content={seoData.og_url || window.location.href} />
      <meta property="og:type" content={seoData.og_type || 'website'} />
      <meta property="og:site_name" content={seoData.site_name || 'Mr. Sea Miami'} />
      <meta property="og:locale" content={seoData.site_locale || 'en_US'} />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content={seoData.twitter_card || 'summary_large_image'} />
      <meta name="twitter:title" content={seoData.twitter_title || seoData.og_title || seoData.title} />
      <meta name="twitter:description" content={seoData.twitter_description || seoData.og_description || seoData.description} />
      <meta name="twitter:image" content={seoData.twitter_image || seoData.og_image} />
      {seoData.twitter_site && <meta name="twitter:site" content={seoData.twitter_site} />}
      {seoData.twitter_creator && <meta name="twitter:creator" content={seoData.twitter_creator} />}
      
      {/* Meta tags adicionales */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="theme-color" content="#006098" />
      
      {/* Structured Data (Schema.org) - Solo para páginas específicas */}
      {seoData.og_type === 'article' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": seoData.title,
            "description": seoData.description,
            "image": seoData.og_image,
            "url": seoData.canonical_url || window.location.href,
            "datePublished": seoData.post_published_at,
            "author": {
              "@type": "Organization",
              "name": "Mr. Sea Miami"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Mr. Sea Miami",
              "url": "https://mrseamiami.com"
            }
          })}
        </script>
      )}
      {/* Landing (Experience) - LocalBusiness + Service */}
      {pageType === 'landing' && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Mr. Sea Miami",
            "description": seoData.description,
            "url": seoData.canonical_url || window.location.href,
            "image": seoData.og_image,
            "address": { "@type": "PostalAddress", "addressLocality": "Miami", "addressRegion": "FL" },
            "areaServed": { "@type": "City", "name": "Miami", "containedInPlace": { "@type": "State", "name": "Florida" } },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Miami Boat Rental Experience",
              "itemListElement": {
                "@type": "Offer",
                "itemOffered": { "@type": "Service", "name": "VIP Miami boat experience" }
              }
            }
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
